import {
  MetricsInterval,
  SENTRY_SERIES_COLORS,
} from 'src/containers/metrics/constants';
import { assignSeriesColors } from 'src/containers/metrics/seriesColors';
import {
  MetricsChartData,
  SentrySeriesUnit,
  SentryTimeseriesResponse,
} from 'src/containers/metrics/types';

import { serializeQueryParameters } from './helpers';
import { monitoringAPI } from './monitoring';
import { getScoringTimeseriesRange } from './scoringHealth';

const UNIT_ORDER: SentrySeriesUnit[] = ['count'];

const UNIT_LABEL: Record<SentrySeriesUnit, string> = {
  count: 'Counts',
};

const toChartTimestamp = (value: number) =>
  value < 1_000_000_000_000 ? value * 1000 : value;

export const fetchSentryTimeseriesByProject = async (
  interval: MetricsInterval
): Promise<SentryTimeseriesResponse> => {
  const { data } = await monitoringAPI.get<SentryTimeseriesResponse>(
    `/sentry/timeseries/by-project${serializeQueryParameters(
      getScoringTimeseriesRange(interval)
    )}`
  );

  return data;
};

export const sentryTimeseriesToCharts = (
  response: SentryTimeseriesResponse,
  interval: MetricsInterval
): MetricsChartData[] => {
  const timestamps = response.timestamps.map(toChartTimestamp);
  const stepMs = response.stepSeconds * 1000;

  return response.apps.flatMap((app) => {
    const byUnit = new Map<string, typeof app.series>();

    app.series.forEach((series) => {
      const current = byUnit.get(series.unit) || [];
      current.push(series);
      byUnit.set(series.unit, current);
    });

    const units = [
      ...UNIT_ORDER.filter((unit) => byUnit.has(unit)),
      ...Array.from(byUnit.keys()).filter(
        (unit) => !UNIT_ORDER.includes(unit as SentrySeriesUnit)
      ),
    ];
    const displayName = app.label || app.slug;

    return units.map((unit) => {
      const items = byUnit.get(unit) || [];
      const colors = assignSeriesColors(items, SENTRY_SERIES_COLORS);

      return {
        id: `${app.targetId}:${unit}`,
        title: `${displayName} · ${
          UNIT_LABEL[unit as SentrySeriesUnit] || unit
        } (${app.status})`,
        interval,
        timestamps,
        stepMs,
        series: items.map((item, index) => ({
          key: item.key,
          name: item.name,
          color: colors[index],
          values: item.values,
          stats: {
            mean: item.stats?.mean ?? null,
            last: item.stats?.last ?? null,
            max: item.stats?.max ?? null,
            total: item.stats?.total ?? null,
          },
        })),
      };
    });
  });
};
